import React, { useEffect, useState } from "react";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import Uri from "jsuri";
import { Input, Button, AlertCard } from "@texada/ui";
import i18n from "i18n/i18n";
import { OK } from "http-status-codes";
import fleetlogicAPI from "helpers/fleetlogicAPI";
import {
  NON_UPGRADED_USER_URL,
  AUTO_DRAFT_UPDATE_INTERVAL,
  GLOBAL_ENV
} from "constants/AppConstants";
import { getApiErrorMsg, getEnvConstant } from "helpers/helper";
import { useInterval } from "helpers/hooks";
import { isValidEmail } from "helpers/validators";
import { getUserStatus } from "./loginUtils";

interface PreLoginTisRegisterProps {
  initialEmail: string;
  tenantId: number | null;
  aux?: string;
  callbackUrl?: string;
  applicationId?: string;
  source?: string;
  tisMandatoryDate?: string | null;
  isIdp?: boolean;
  onSkip?: () => void;
}

const PreLoginTisRegister = ({
  initialEmail,
  tenantId,
  aux,
  callbackUrl,
  applicationId,
  source,
  tisMandatoryDate,
  isIdp = false,
  onSkip
}: PreLoginTisRegisterProps) => {
  const isForceMigration =
    tisMandatoryDate != null && new Date() >= new Date(tisMandatoryDate);
  const [email, setEmail] = useState(initialEmail || "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!submitted || isLoading) return;
      if (!document.hidden) {
        checkEmailVerifyStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [submitted, isLoading]);

  useInterval(() => {
    if (!submitted || isLoading) return;
    return checkEmailVerifyStatus();
  }, AUTO_DRAFT_UPDATE_INTERVAL);

  const checkEmailVerifyStatus = async () => {
    const result = await getUserStatus(email);
    if (result?.email_verified !== true) return false;
    setSuccess(i18n.t("your_email_has_been_verified"));
    onRedirectDebounce();
    return true;
  };

  const redirectToTis = () => {
    const returnUrl = new Uri(`${window.location.origin}/verify`).addQueryParam(
      "tenant",
      tenantId
    );

    if (callbackUrl) {
      returnUrl.addQueryParam("callbackUrl", callbackUrl);
    }
    if (source) {
      returnUrl.addQueryParam("source", source);
    }
    if (applicationId) {
      returnUrl.addQueryParam("applicationId", applicationId);
    }

    const tisLoginUrl = new Uri(getEnvConstant(GLOBAL_ENV.TIS_LOGIN_URL))
      .addQueryParam("environment", getEnvConstant(GLOBAL_ENV.TIS_ENVIRONMENT))
      .addQueryParam("returnUrl", returnUrl.toString())
      .addQueryParam("email", email)
      .addQueryParam("tenantId", tenantId);

    if (aux) {
      tisLoginUrl.addQueryParam("aux", aux);
    }

    window.location.href = tisLoginUrl.toString();
  };

  const onRedirectDebounce = debounce(redirectToTis, 2000);

  const validateEmail = () => {
    if (isEmpty(email)) {
      setError(i18n.t("field_is_required", { field: i18n.t("email") }));
      return false;
    }
    if (!isValidEmail(email)) {
      setError(i18n.t("invalid_email_address"));
      return false;
    }
    setError("");
    return true;
  };

  const preventDefault = callback => e => {
    e.preventDefault();
    callback();
  };

  const onSubmit = async () => {
    setIsLoading(true);
    if (!validateEmail()) {
      setIsLoading(false);
      return;
    }
    if (submitted) {
      await resendVerificationEmail();
      return;
    }
    await inviteUser();
  };

  const inviteUser = async () => {
    try {
      const res = await fleetlogicAPI.post(
        `${NON_UPGRADED_USER_URL}/pre-login-invite`,
        { email, tenant_id: tenantId }
      );

      if (res.status === OK || res?.data?.UserId || res?.data?.is_idp) {
        // IDP companies don't need email verification — redirect immediately
        if (res.data.email_verified === true || res.data.is_idp) {
          setSuccess(i18n.t("your_email_has_been_verified"));
          onRedirectDebounce();
          return;
        }
        if (!res.data.hasOwnProperty("UserId")) {
          const verified = await checkEmailVerifyStatus();
          if (verified) return;
        }
        setSuccess(i18n.t("we_have_just_sent_an_verification_email"));
        setSubmitted(true);
      } else {
        setError(i18n.t("error_to_migrate_user"));
      }
      setIsLoading(false);
    } catch (err) {
      setError(getApiErrorMsg(err));
      setIsLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    try {
      await fleetlogicAPI.post(`${NON_UPGRADED_USER_URL}/pre-login-verify`, {
        email,
        tenant_id: tenantId
      });
      setSuccess(i18n.t("we_have_just_sent_an_verification_email"));
      setIsLoading(false);
    } catch (err) {
      const isVerified = await checkEmailVerifyStatus();
      if (isVerified) return;
      setError(getApiErrorMsg(err));
      setIsLoading(false);
    }
  };

  return (
    <form className="login_form" onSubmit={preventDefault(onSubmit)}>
      <AlertCard type="success" timeout={null} closable={false} size="full">
        {isForceMigration
          ? i18n.t(
              "our_login_process_has_changed_click_the_button_below_to_reset_your_password_and_migrate_to_texada_identity_service_tis"
            )
          : i18n.t(
              "tis_is_here_bringing_with_it_increased_security_and_a_streamlined_login_process_click_the_button_below_to_begin_your_migration"
            )}
      </AlertCard>
      <Input
        className="email"
        field="email"
        id="email"
        label={i18n.t("update_your_email_address_here")}
        value={email}
        error={error}
        onChange={e => setEmail(e.target.value)}
        inputProps={{ autoCapitalize: "off" }}
        required
        disabled={isLoading}
      />
      {success && <p className="success-message">{success}</p>}
      <Button
        className="login-button"
        isLoading={isLoading}
        buttonProps={{ type: "submit" }}
      >
        {isIdp || !submitted
          ? i18n.t("set_up_now")
          : i18n.t("resend_verification_email")}
      </Button>
      {!isForceMigration && onSkip && (
        <div className="button-link-container">
          <button type="button" onClick={onSkip} className="login-link">
            {i18n.t("no_skip_it")}
          </button>
        </div>
      )}
    </form>
  );
};

export default PreLoginTisRegister;
